import React, { useState } from 'react';
import { AlertTriangle, Eraser, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

// Super-admin: find LEI/KPI rows the current seeds don't own and purge them so
// the trend charts share one clean monthly window.
export default function LegacyHistoryPanel({ organizations, onReseed }) {
  const [orgId, setOrgId] = useState('');
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [reseed, setReseed] = useState(true);
  const [result, setResult] = useState(null);

  const loadPreview = async () => {
    if (!orgId) return;
    setLoading(true); setResult(null);
    try {
      const r = await api.get(`/admin/seed/legacy-history?organization_id=${encodeURIComponent(orgId)}`);
      setPreview(r.data);
      setSelected(r.data.groups.filter((g) => !g.is_import).map((g) => g.key));
    } catch (err) { toast.error(`Preview failed: ${err.response?.data?.detail || err.message}`); }
    finally { setLoading(false); }
  };

  const toggle = (key) => setSelected((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));

  const purge = async () => {
    const rows = preview.groups.filter((g) => selected.includes(g.key)).reduce((s, g) => s + g.count, 0);
    if (!window.confirm(`Permanently delete ${rows.toLocaleString()} legacy rows from ${preview.organization.name}? This cannot be undone.`)) return;
    setPurging(true);
    try {
      const r = await api.post('/admin/seed/legacy-history/purge', { organization_id: orgId, group_keys: selected });
      setResult(r.data);
      toast.success(`Purged ${r.data.total_deleted.toLocaleString()} legacy rows`);
      if (reseed) await onReseed();
      await loadPreview();
    } catch (err) { toast.error(`Purge failed: ${err.response?.data?.detail || err.message}`); }
    finally { setPurging(false); }
  };

  const selectedRows = preview ? preview.groups.filter((g) => selected.includes(g.key)).reduce((s, g) => s + g.count, 0) : 0;

  return (
    <div className="space-y-3 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="legacy-history-panel">
      <div>
        <h4 className="text-sm font-bold text-amber-900 inline-flex items-center gap-1.5"><Eraser className="h-4 w-4" /> Purge legacy LEI / KPI history</h4>
        <p className="text-xs text-amber-800 mt-0.5">Older imports and untagged rows sit underneath the current seed and stretch the LEI vs KPI timelines apart. Preview what the seeds don&apos;t own, untick anything you want to keep, then purge{reseed ? ' and reseed' : ''}.</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setPreview(null); setResult(null); }} className="h-8 rounded-md border border-amber-300 bg-white px-2 text-xs" data-testid="legacy-history-org">
          <option value="">Select organisation…</option>
          {organizations.map((o) => <option key={o.org_id} value={o.org_id}>{o.org_name}</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={loadPreview} disabled={!orgId || loading} className="border-amber-300" data-testid="legacy-history-preview-btn">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Search className="h-3.5 w-3.5 mr-1.5" />}Preview
        </Button>
        {preview && <span className="text-xs text-amber-800" data-testid="legacy-history-window">Current seed window: {preview.seed_window.start} → {preview.seed_window.end}</span>}
      </div>

      {preview && preview.groups.length === 0 && <p className="text-xs text-emerald-800 bg-emerald-100 rounded p-2" data-testid="legacy-history-clean">No legacy rows — every LEI/KPI record here belongs to the current seed.</p>}

      {preview && preview.groups.length > 0 && (
        <ul className="divide-y divide-amber-200 rounded-md border border-amber-200 bg-white text-xs" data-testid="legacy-history-groups">
          {preview.groups.map((g) => (
            <li key={g.key} className="flex items-center gap-3 px-3 py-2">
              <input type="checkbox" checked={selected.includes(g.key)} onChange={() => toggle(g.key)} className="rounded" data-testid={`legacy-history-group-${g.key}`} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800 truncate">{g.label}</p>
                <p className="text-slate-500">{g.count.toLocaleString()} rows · {g.first_month && g.last_month ? `${g.first_month} → ${g.last_month}` : 'no usable dates'}{g.unparseable_dates ? ` · ${g.unparseable_dates} unparseable dates` : ''}</p>
              </div>
              {g.is_import && <Badge variant="outline" className="text-[10px] text-indigo-700 border-indigo-300" data-testid={`legacy-history-import-${g.key}`}>real import · kept by default</Badge>}
            </li>
          ))}
        </ul>
      )}

      {preview && preview.groups.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <Button size="sm" onClick={purge} disabled={purging || selected.length === 0} className="bg-amber-600 hover:bg-amber-700" data-testid="legacy-history-purge-btn">
            {purging ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />}
            Purge {selectedRows.toLocaleString()} rows{reseed ? ' & reseed' : ''}
          </Button>
          <label className="flex items-center gap-1.5 text-xs text-amber-900">
            <input type="checkbox" checked={reseed} onChange={(e) => setReseed(e.target.checked)} className="rounded" data-testid="legacy-history-reseed" />
            Run “Seed LEI/PE/KPI Data” afterwards
          </label>
        </div>
      )}

      {result && (
        <p className="text-xs text-emerald-800 bg-emerald-100 rounded p-2" data-testid="legacy-history-result">
          Deleted {result.total_deleted.toLocaleString()} rows from {result.organization.name}{reseed ? ' — reseed started, progress shows above.' : '.'}
        </p>
      )}
    </div>
  );
}
